import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, LabelList } from "recharts";
import { addMonths } from "date-fns";
import { ShieldCheckIcon as ShieldCheck, MagnifyingGlassIcon as MagnifyingGlass, PlusIcon as Plus, FileTextIcon as FileText, ArrowClockwiseIcon as ArrowClockwise, FloppyDiskIcon as FloppyDisk, CheckCircleIcon as CheckCircle, BuildingsIcon as Buildings, ClipboardTextIcon as ClipboardText, ChartBarIcon as ChartBar, CaretRightIcon as CaretRight, ArchiveIcon as Archive, ClockIcon as Clock, CaretDownIcon as CaretDown, X, WarningCircleIcon as WarningCircle, CalendarBlankIcon as CalendarBlank, ArrowsOutIcon as ArrowsOut, ArrowsInIcon as ArrowsIn, PaperclipIcon as Paperclip, TrashIcon as Trash, PencilSimpleIcon as PencilSimple, UploadSimpleIcon as UploadSimple, DownloadSimpleIcon as DownloadSimple, LinkSimpleIcon as LinkSimple, WarningOctagonIcon as WarningOctagon, ListChecksIcon as ListChecks } from "@phosphor-icons/react";

const NIS2_STATI_RISPOSTA = [
  { v: "conforme", l: "Conforme", c: "text-emerald-600" },
  { v: "parzialmente_conforme", l: "Parzialmente conforme", c: "text-amber-600" },
  { v: "non_conforme", l: "Non conforme", c: "text-red-600" },
  { v: "non_verificato", l: "Non verificato", c: "text-slate-500" },
  { v: "non_applicabile", l: "Non applicabile motivato", c: "text-slate-400" },
];
const NIS2_STATO_RISP_LABEL = Object.fromEntries(NIS2_STATI_RISPOSTA.map((s) => [s.v, s.l]));
const NIS2_PRIORITA = [
  { v: "critica", l: "Critica" }, { v: "alta", l: "Alta" }, { v: "media", l: "Media" }, { v: "bassa", l: "Bassa" },
];
const NIS2_OWNERS = ["Tecnologie", "Cliente", "Legale", "HR"];
const NIS2_ACTION_STATI = [
  { v: "da_pianificare", l: "Da pianificare" }, { v: "pianificata", l: "Pianificata" },
  { v: "in_corso", l: "In corso" }, { v: "completata", l: "Completata" }, { v: "annullata", l: "Annullata" },
];
const NIS2_ACTION_STATO_LABEL = Object.fromEntries(NIS2_ACTION_STATI.map((s) => [s.v, s.l]));
const nis2Today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };

const STATO_BADGE = {
  bozza: "bg-slate-100 text-slate-600",
  in_corso: "bg-amber-100 text-amber-700",
  finalizzata: "bg-emerald-100 text-emerald-700",
  archiviata: "bg-slate-200 text-slate-500",
};

function StatoBadge({ stato, label }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${STATO_BADGE[stato] || "bg-slate-100 text-slate-600"}`} data-testid="nis2-stato-badge">{label}</span>;
}

// Data civile (YYYY-MM-DD) timezone-safe via componenti locali
function DateField({ value, onChange, disabled, testid }) {
  const [open, setOpen] = useState(false);
  const parsed = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="mt-1 w-full justify-start rounded-sm h-9 text-sm font-normal" data-testid={testid}>
          <CalendarBlank size={14} className="mr-2 text-muted-foreground" /> {parsed ? parsed.toLocaleDateString("it-IT") : "Seleziona data"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={parsed} onSelect={(d) => {
          if (d) {
            const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
            onChange(`${y}-${m}-${day}`);
          }
          setOpen(false);
        }} />
      </PopoverContent>
    </Popover>
  );
}

const nis2FmtStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const nis2AddMonths = (dateStr, n) => {
  if (!dateStr) return "";
  try { return nis2FmtStr(addMonths(new Date(`${dateStr}T00:00:00`), n)); } catch { return ""; }
};

// Editor perimetro STRUTTURATO (questionario v2): text/number/date/textarea/select/tags/array/sedi + termini adeguamento
function PerimetroV2({ schema, perim, onChange, disabled, clientSedi }) {
  const groups = useMemo(() => {
    const byG = {};
    (schema || []).forEach((f) => { (byG[f.gruppo] = byG[f.gruppo] || []).push(f); });
    return byG;
  }, [schema]);
  const gruppiOrdine = ["Dati cliente", "Perimetro tecnico", "Limiti", "Termini di adeguamento"];
  const ordered = Object.keys(groups).sort((a, b) => gruppiOrdine.indexOf(a) - gruppiOrdine.indexOf(b));

  return (
    <div className="space-y-5" data-testid="nis2-perimetro-v2">
      {ordered.map((gruppo) => (
        <div key={gruppo}>
          <div className="text-[11px] uppercase tracking-wider text-primary font-semibold mb-2">{gruppo}</div>
          {gruppo === "Termini di adeguamento" ? (
            <TerminiAdeguamento perim={perim} onChange={onChange} disabled={disabled} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {groups[gruppo].map((f) => (
                <PerimField key={f.key} f={f} perim={perim} set={onChange} disabled={disabled} clientSedi={clientSedi} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PerimField({ f, perim, set, disabled, clientSedi }) {
  const value = perim[f.key];
  const onChange = (v) => set(f.key, v);
  const full = ["textarea", "array", "sedi", "tags"].includes(f.type);
  const showAltro = (f.type === "select" || f.type === "combobox") && f.altro_key && value === "Altro";
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
      {f.type === "textarea" ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-1 rounded-sm text-sm min-h-[60px]" data-testid={`nis2-perim-${f.key}`} />
      ) : f.type === "number" ? (
        <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-1 rounded-sm h-9 text-sm" data-testid={`nis2-perim-${f.key}`} />
      ) : f.type === "date" ? (
        <DateField value={value || ""} onChange={onChange} disabled={disabled} testid={`nis2-perim-${f.key}`} />
      ) : (f.type === "select" || f.type === "combobox") ? (
        <>
          <Select value={value || ""} onValueChange={(v) => onChange(v === "__none" ? "" : v)} disabled={disabled}>
            <SelectTrigger className="mt-1 h-9 rounded-sm text-sm" data-testid={`nis2-perim-${f.key}`}><SelectValue placeholder="— seleziona —" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="__none">— non specificato —</SelectItem>
              {(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          {showAltro && (
            <Input value={perim[f.altro_key] || ""} onChange={(e) => set(f.altro_key, e.target.value)} disabled={disabled} placeholder={f.altro_label || "Specifica"} className="mt-1.5 rounded-sm h-9 text-sm" data-testid={`nis2-perim-${f.altro_key}`} />
          )}
        </>
      ) : f.type === "tags" ? (
        <TagsField value={value} onChange={onChange} disabled={disabled} testid={`nis2-perim-${f.key}`} />
      ) : f.type === "sedi" ? (
        <SediField value={value} onChange={onChange} disabled={disabled} clientSedi={clientSedi} testid={`nis2-perim-${f.key}`} />
      ) : f.type === "array" ? (
        <ArrayField f={f} value={value} onChange={onChange} disabled={disabled} />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} disabled={disabled} className="mt-1 rounded-sm h-9 text-sm" data-testid={`nis2-perim-${f.key}`} />
      )}
    </div>
  );
}

function TagsField({ value, onChange, disabled, testid }) {
  const [txt, setTxt] = useState("");
  const arr = Array.isArray(value) ? value : (value ? String(value).split(",").map((s) => s.trim()).filter(Boolean) : []);
  const add = () => { const v = txt.trim(); if (v && !arr.includes(v)) { onChange([...arr, v]); } setTxt(""); };
  return (
    <div className="mt-1" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {arr.map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 text-xs" data-testid={`${testid}-tag-${i}`}>
            {t}{!disabled && <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))}><X size={11} /></button>}
          </span>
        ))}
        {arr.length === 0 && <span className="text-xs text-muted-foreground">Nessun valore</span>}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="rounded-sm h-8 text-sm" placeholder="Aggiungi e premi Invio" data-testid={`${testid}-input`} />
          <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" onClick={add} data-testid={`${testid}-add`}><Plus size={13} /></Button>
        </div>
      )}
    </div>
  );
}

function SediField({ value, onChange, disabled, clientSedi, testid }) {
  const [txt, setTxt] = useState("");
  const arr = Array.isArray(value) ? value : (value ? [String(value)] : []);
  const opts = Array.from(new Set([...(clientSedi || []), ...arr.filter((s) => s !== "Tutte le sedi")]));
  const all = arr.includes("Tutte le sedi");
  const toggle = (s) => { onChange(all ? [s] : (arr.includes(s) ? arr.filter((x) => x !== s) : [...arr, s])); };
  const toggleAll = () => { onChange(all ? [] : ["Tutte le sedi"]); };
  const add = () => { const v = txt.trim(); if (v && !arr.includes(v)) onChange([...arr.filter((x) => x !== "Tutte le sedi"), v]); setTxt(""); };
  return (
    <div className="mt-1 space-y-1.5" data-testid={testid}>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={all} onCheckedChange={toggleAll} disabled={disabled} data-testid={`${testid}-all`} /> Tutte le sedi
      </label>
      {!all && opts.map((s, i) => (
        <label key={i} className="flex items-center gap-2 text-sm" data-testid={`${testid}-opt-${i}`}>
          <Checkbox checked={arr.includes(s)} onCheckedChange={() => toggle(s)} disabled={disabled} /> {s}
        </label>
      ))}
      {!all && opts.length === 0 && <span className="text-xs text-muted-foreground">Nessuna sede in anagrafica</span>}
      {!disabled && !all && (
        <div className="flex gap-2 pt-1">
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="rounded-sm h-8 text-sm" placeholder="Aggiungi sede solo per questo assessment" data-testid={`${testid}-input`} />
          <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" onClick={add} data-testid={`${testid}-add`}><Plus size={13} /></Button>
        </div>
      )}
    </div>
  );
}

function ArrayField({ f, value, onChange, disabled }) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => onChange([...rows, Object.fromEntries(f.item_fields.map((it) => [it.key, ""]))]);
  const delRow = (i) => onChange(rows.filter((_, j) => j !== i));
  return (
    <div className="mt-1 space-y-2" data-testid={`nis2-perim-${f.key}`}>
      {rows.map((r, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2 rounded-sm border border-border p-2" data-testid={`nis2-perim-${f.key}-row-${i}`}>
          {f.item_fields.map((it) => (
            <div key={it.key} className="flex-1 min-w-[140px]">
              <Label className="text-[10px] text-muted-foreground">{it.label}</Label>
              <Input value={r[it.key] || ""} onChange={(e) => setRow(i, it.key, e.target.value)} disabled={disabled} className="mt-0.5 rounded-sm h-8 text-sm" data-testid={`nis2-perim-${f.key}-${i}-${it.key}`} />
            </div>
          ))}
          {!disabled && <Button type="button" variant="ghost" size="sm" className="rounded-sm h-8 text-red-500" onClick={() => delRow(i)} data-testid={`nis2-perim-${f.key}-del-${i}`}><Trash size={14} /></Button>}
        </div>
      ))}
      {rows.length === 0 && <span className="text-xs text-muted-foreground">Nessuna voce</span>}
      {!disabled && <Button type="button" variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" onClick={addRow} data-testid={`nis2-perim-${f.key}-add`}><Plus size={13} /> Aggiungi</Button>}
    </div>
  );
}

function TerminiAdeguamento({ perim, onChange, disabled }) {
  const acn = perim.data_comunicazione_acn || "";
  const is2026 = acn && new Date(`${acn}T00:00:00`).getFullYear() === 2026;
  const setAcn = (v) => {
    onChange("data_comunicazione_acn", v);
    // Auto-proposta standard SOLO se le scadenze sono vuote (nessuna sovrascrittura di date già inserite)
    if (v && !perim.scadenza_misure && !perim.scadenza_notifica) {
      onChange("scadenza_misure", nis2AddMonths(v, 18));
      onChange("scadenza_notifica", nis2AddMonths(v, 9));
    }
  };
  const calcStandard = () => {
    if (!acn) { toast.info("Inserisci prima la data di comunicazione ACN"); return; }
    onChange("scadenza_misure", nis2AddMonths(acn, 18));
    onChange("scadenza_notifica", nis2AddMonths(acn, 9));
    toast.success("Scadenze calcolate (+18 / +9 mesi)");
  };
  const applySpeciali = () => {
    if (!window.confirm("Applicare le scadenze speciali per i soggetti inseriti la prima volta nel 2026?\n· Misure di sicurezza: 31/07/2027\n· Obbligo notifica incidenti: 01/01/2027\n\nLe date attuali verranno sostituite.")) return;
    onChange("scadenza_misure", "2027-07-31");
    onChange("scadenza_notifica", "2027-01-01");
    toast.success("Applicate le date speciali 2026");
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] text-muted-foreground">Data comunicazione ACN</Label>
          <DateField value={acn} onChange={setAcn} disabled={disabled} testid="nis2-perim-data_comunicazione_acn" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Scadenza misure di sicurezza (+18 mesi)</Label>
          <DateField value={perim.scadenza_misure || ""} onChange={(v) => onChange("scadenza_misure", v)} disabled={disabled} testid="nis2-perim-scadenza_misure" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Scadenza obbligo notifica incidenti (+9 mesi)</Label>
          <DateField value={perim.scadenza_notifica || ""} onChange={(v) => onChange("scadenza_notifica", v)} disabled={disabled} testid="nis2-perim-scadenza_notifica" />
        </div>
      </div>
      {!disabled && (
        <Button type="button" variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" onClick={calcStandard} data-testid="nis2-calc-scadenze"><ArrowClockwise size={13} /> Calcola scadenze (+18 / +9 mesi)</Button>
      )}
      {is2026 && !disabled && (
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="nis2-termini-2026-alert">
          <div className="flex items-start gap-2">
            <WarningCircle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Soggetto inserito nel 2026: possibili scadenze speciali</p>
              <p className="mt-0.5">Per i soggetti inseriti la prima volta nel 2026 la norma prevede: misure di sicurezza entro <b>31/07/2027</b> e obbligo di notifica entro <b>01/01/2027</b>. Le date NON vengono applicate automaticamente.</p>
              <Button type="button" size="sm" className="rounded-sm h-7 mt-2 text-xs" onClick={applySpeciali} data-testid="nis2-apply-2026">Applica date speciali 2026</Button>
            </div>
          </div>
        </div>
      )}
      <p className="text-[11px] text-muted-foreground italic border-t border-border pt-2">
        Le date proposte sono un supporto operativo per la pianificazione e non costituiscono un parere legale. Verifica sempre la comunicazione ufficiale ACN.
      </p>
    </div>
  );
}

const TABS = [
  { key: "cliente", label: "Cliente e analisi", icon: Buildings },
  { key: "questionario", label: "Questionario", icon: ClipboardText },
  { key: "risultati", label: "Risultati", icon: ChartBar },
];

export default function Nis2() {
  const { user } = useAuth();
  const [tab, setTab] = useState("cliente");
  const [focusNum, setFocusNum] = useState(null);
  const [master, setMaster] = useState(null);
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [selClient, setSelClient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [loadingA, setLoadingA] = useState(false);
  const [rev, setRev] = useState(null);
  const [perim, setPerim] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => { api.get("/nis2/master").then(({ data }) => setMaster(data)).catch(() => {}); }, []);

  // Deep link: /tools/nis2?assessment_id=...&revision_id=...
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const rid = p.get("revision_id");
    if (!rid) return;
    (async () => {
      try {
        const { data: r } = await api.get(`/nis2/revisions/${rid}`);
        const cid = r.cliente_id;
        const { data: ass } = await api.get("/nis2/assessments", { params: { cliente_id: cid } });
        setAssessments(ass || []);
        setSelClient({ id: cid, ragione_sociale: r.cliente_nome || ass?.[0]?.cliente_nome || "Cliente" });
        setRev(r); setPerim(r.perimetro || {}); setLastSaved(r.updated_at); setDirty(false);
        setTab("risultati");
      } catch { /* ignore */ }
    })();
  }, []);

  const searchClients = useCallback((term) => {
    api.get("/nis2/clients", { params: term ? { q: term } : {} }).then(({ data }) => setClients(data || [])).catch(() => setClients([]));
  }, []);
  useEffect(() => { const t = setTimeout(() => searchClients(q), 300); return () => clearTimeout(t); }, [q, searchClients]);

  const loadAssessments = useCallback((cid) => {
    setLoadingA(true);
    api.get("/nis2/assessments", { params: { cliente_id: cid } })
      .then(({ data }) => setAssessments(data || [])).catch(() => setAssessments([]))
      .finally(() => setLoadingA(false));
  }, []);

  const pickClient = (c) => { setSelClient(c); setRev(null); loadAssessments(c.id); };

  const createAssessment = async () => {
    if (!selClient) return;
    try {
      const { data } = await api.post("/nis2/assessments", { cliente_id: selClient.id });
      toast.success("Analisi creata");
      loadAssessments(selClient.id);
      openRevision(data.revision_id);
    } catch { toast.error("Errore nella creazione dell'analisi"); }
  };

  const openRevision = async (rid) => {
    try {
      const { data } = await api.get(`/nis2/revisions/${rid}`);
      setRev(data);
      setPerim(data.perimetro || {});
      setDirty(false);
      setLastSaved(data.updated_at);
    } catch { toast.error("Impossibile aprire la revisione"); }
  };

  const savePerimetro = async () => {
    if (!rev || rev.immutabile) return;
    setSaving(true);
    try {
      await api.put(`/nis2/revisions/${rev.id}/perimetro`, { perimetro: perim });
      setDirty(false);
      setLastSaved(new Date().toISOString());
      toast.success("Perimetro salvato");
      if (selClient) loadAssessments(selClient.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const setField = (k, v) => { setPerim((s) => ({ ...s, [k]: v })); setDirty(true); };

  const newRevisionFrom = async (rid) => {
    try {
      const { data } = await api.post(`/nis2/revisions/${rid}/new-revision`);
      toast.success("Nuova revisione creata dalla precedente");
      if (selClient) loadAssessments(selClient.id);
      openRevision(data.revision_id);
    } catch { toast.error("Errore nella creazione della revisione"); }
  };

  const newEmptyRevision = async (aid) => {
    try {
      const { data } = await api.post(`/nis2/assessments/${aid}/revisions`);
      toast.success("Nuova revisione vuota creata");
      if (selClient) loadAssessments(selClient.id);
      openRevision(data.revision_id);
    } catch { toast.error("Errore nella creazione della revisione"); }
  };

  const questFlushRef = useRef(null);
  const changeTab = useCallback(async (t) => {
    if (t === tab) return;
    if (tab === "questionario" && questFlushRef.current) {
      try { await questFlushRef.current(); } catch { /* errore già notificato */ }
    }
    setTab(t);
  }, [tab]);

  const [delTarget, setDelTarget] = useState(null); // { assessment, preview }
  const [delText, setDelText] = useState("");
  const [delBusy, setDelBusy] = useState(false);

  const openDeletePreview = async (a) => {
    setDelText("");
    try {
      const { data } = await api.get(`/nis2/assessments/${a.id}/delete-preview`);
      setDelTarget({ assessment: a, preview: data });
    } catch { toast.error("Impossibile preparare l'eliminazione"); }
  };

  const confirmDelete = async () => {
    if (!delTarget || delText.trim().toUpperCase() !== "ELIMINA") return;
    setDelBusy(true);
    try {
      const { data } = await api.delete(`/nis2/assessments/${delTarget.assessment.id}`);
      const removedId = delTarget.assessment.id;
      setAssessments((arr) => arr.filter((x) => x.id !== removedId));
      if (rev && rev.assessment_id === removedId) { setRev(null); setPerim({}); }
      setDelTarget(null); setDelText("");
      const e = data?.eliminati || {};
      toast.success(`Analisi eliminata (${e.revisioni || 0} revisioni, ${e.risposte || 0} risposte, ${e.allegati || 0} allegati)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione");
    } finally { setDelBusy(false); }
  };

  const [delRev, setDelRev] = useState(null); // { preview }
  const [delRevText, setDelRevText] = useState("");
  const [delRevBusy, setDelRevBusy] = useState(false);
  const openDelRevPreview = async (revisionId) => {
    setDelRevText("");
    try {
      const { data } = await api.get(`/nis2/revisions/${revisionId}/delete-preview`);
      setDelRev(data);
    } catch { toast.error("Impossibile preparare l'eliminazione della revisione"); }
  };
  const confirmDelRev = async () => {
    if (!delRev || delRevText.trim().toUpperCase() !== "ELIMINA") return;
    setDelRevBusy(true);
    try {
      const { data } = await api.delete(`/nis2/revisions/${delRev.revision_id}`);
      const removedId = delRev.revision_id;
      if (rev && rev.id === removedId) { setRev(null); setPerim({}); setTab("cliente"); }
      if (selClient) loadAssessments(selClient.id);
      setDelRev(null); setDelRevText("");
      const e = data?.eliminati || {};
      toast.success(`Revisione eliminata (${e.risposte || 0} risposte, ${e.allegati || 0} allegati, ${e.azioni || 0} azioni)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'eliminazione della revisione");
    } finally { setDelRevBusy(false); }
  };

  const setStato = async (stato) => {
    if (!rev) return;
    try {
      await api.patch(`/nis2/revisions/${rev.id}/stato`, { stato });
      toast.success("Stato aggiornato");
      openRevision(rev.id);
      if (selClient) loadAssessments(selClient.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel cambio stato"); }
  };

  const groups = useMemo(() => {
    const fields = (rev?.perimetro_version === 1 && rev?.perimetro_schema) ? rev.perimetro_schema : (rev?.perimetro_schema || master?.perimetro_fields || []);
    const byG = {};
    fields.forEach((f) => { (byG[f.gruppo] = byG[f.gruppo] || []).push(f); });
    return byG;
  }, [master, rev]);

  const clientSedi = useMemo(() => (selClient?.sedi || []).map((s) => (typeof s === "string" ? s : (s.nome || s.indirizzo || s.citta || ""))).filter(Boolean), [selClient]);

  const fmtDT = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };

  return (
    <InternalLayout>
      <div className="p-6 lg:p-8">
        <PageHeader title="NIS2" subtitle="Assessment NIS2 preliminare sui clienti — questionario, evidenze e report" />
          <div className="flex items-center gap-1 border-b border-border mb-6" data-testid="nis2-topmenu">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => changeTab(t.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`nis2-tab-${t.key}`}>
                  <Icon size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          {tab === "cliente" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Colonna sinistra: ricerca cliente + analisi */}
              <div className="lg:col-span-1 space-y-4">
                <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-client-picker">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cliente</Label>
                  <div className="relative mt-1 mb-2">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ragione sociale, P.IVA, C.F.…" className="rounded-sm h-9 pl-8" data-testid="nis2-client-search" />
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1">
                    {clients.slice(0, 50).map((c) => (
                      <button key={c.id} onClick={() => pickClient(c)}
                        className={`w-full text-left px-2.5 py-1.5 rounded-sm text-sm transition-colors ${selClient?.id === c.id ? "bg-primary/10 text-primary" : "hover:bg-secondary"}`}
                        data-testid={`nis2-client-opt-${c.id}`}>
                        <div className="font-medium truncate">{c.ragione_sociale}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{c.partita_iva || c.codice_fiscale || c.citta || ""}</div>
                      </button>
                    ))}
                    {clients.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">Nessun cliente</p>}
                  </div>
                </Card>

                {selClient && (
                  <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-client-summary">
                    <div className="flex items-center gap-2 mb-2"><Buildings size={16} className="text-primary" /><h3 className="font-heading text-sm font-semibold">{selClient.ragione_sociale}</h3></div>
                    <dl className="text-xs space-y-1 text-muted-foreground">
                      {selClient.partita_iva && <div>P.IVA: <span className="text-foreground">{selClient.partita_iva}</span></div>}
                      {selClient.codice_fiscale && <div>C.F.: <span className="text-foreground">{selClient.codice_fiscale}</span></div>}
                      {selClient.citta && <div>Città: <span className="text-foreground">{selClient.citta}</span></div>}
                      {selClient.settore && <div>Settore: <span className="text-foreground">{selClient.settore}</span></div>}
                    </dl>
                    <Button className="w-full mt-3 rounded-sm h-9 gap-1.5" onClick={createAssessment} data-testid="nis2-new-assessment"><Plus size={15} /> Nuova analisi NIS2</Button>
                  </Card>
                )}
              </div>

              {/* Colonna destra: elenco analisi/revisioni + editor perimetro */}
              <div className="lg:col-span-2 space-y-4">
                {!selClient ? (
                  <Card className="rounded-xl border-dashed border-slate-200 p-10 text-center text-sm text-muted-foreground" data-testid="nis2-empty-state">
                    Seleziona un cliente per creare o aprire un'analisi NIS2.
                  </Card>
                ) : (
                  <>
                    <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-assessments-list">
                      <h3 className="font-heading text-sm font-semibold mb-3 flex items-center gap-1.5"><FileText size={16} className="text-primary" /> Analisi del cliente</h3>
                      {loadingA ? <Skeleton className="h-20 rounded-lg" /> : assessments.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-4">Nessuna analisi. Crea la prima con "Nuova analisi NIS2".</p>
                      ) : (
                        <div className="space-y-3">
                          {assessments.map((a) => {
                            const rv = a.revisioni?.[0];
                            const ver = rv?.versione_questionario;
                            const nd = rv?.num_domande;
                            return (
                            <div key={a.id} className="border border-border rounded-lg p-3" data-testid={`nis2-assessment-${a.id}`}>
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="font-medium text-sm truncate">{a.titolo}</div>
                                  {ver && <span className="shrink-0 inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold" data-testid={`nis2-ass-versione-${a.id}`}>{ver === 2 ? "Questionario v2 · 70 domande" : `Questionario storico v1 · ${nd || 51} domande`}</span>}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <Button variant="outline" size="sm" className="rounded-sm h-7 gap-1 text-xs" onClick={() => newEmptyRevision(a.id)} data-testid={`nis2-new-empty-rev-${a.id}`}><Plus size={13} /> Revisione vuota</Button>
                                  <Button variant="ghost" size="sm" className="rounded-sm h-7 px-2 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => openDeletePreview(a)} data-testid={`nis2-delete-assessment-${a.id}`}><Trash size={14} /></Button>
                                </div>
                              </div>
                              <div className="space-y-1">
                                {a.revisioni.map((r) => (
                                  <div key={r.id} className={`flex items-center gap-1 rounded-sm ${rev?.id === r.id ? "bg-primary/10" : "hover:bg-secondary"}`}>
                                    <button onClick={() => openRevision(r.id)}
                                      className="flex-1 flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm transition-colors min-w-0"
                                      data-testid={`nis2-rev-${r.id}`}>
                                      <span className="flex items-center gap-2 min-w-0"><CaretRight size={12} className="shrink-0" /> <span className="truncate">{r.revisione_label}</span>
                                        <span className="shrink-0 text-[10px] text-muted-foreground">v{r.versione_questionario} · {r.num_domande} dom.</span>
                                        {r.coerente === false && <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] text-red-600 font-medium" data-testid={`nis2-rev-incoerente-${r.id}`}><WarningOctagon size={11} /> incoerente</span>}
                                      </span>
                                      <span className="flex items-center gap-2 shrink-0">
                                        <StatoBadge stato={r.stato} label={r.stato_label} />
                                        <span className="text-[11px] text-muted-foreground">{fmtDT(r.updated_at)}</span>
                                      </span>
                                    </button>
                                    {(r.stato === "bozza" || r.stato === "in_corso") && (
                                      <button onClick={(e) => { e.stopPropagation(); openDelRevPreview(r.id); }} title="Elimina revisione"
                                        className="shrink-0 mr-1 p-1 rounded-sm text-red-500 hover:text-red-600 hover:bg-red-50" data-testid={`nis2-delete-rev-${r.id}`}><Trash size={13} /></button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </Card>

                    {rev && (
                      <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-perimetro-editor">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2">
                            <h3 className="font-heading text-sm font-semibold">Perimetro — {rev.revisione_label}</h3>
                            <span className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold" data-testid="nis2-versione-badge">Questionario v{rev.perimetro_version || rev.versione_questionario} · {rev.num_domande} domande</span>
                            <StatoBadge stato={rev.stato} label={rev.stato_label} />
                            {rev.immutabile && <span className="text-[11px] text-amber-600 font-medium">Revisione immutabile</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            {!rev.immutabile && rev.stato === "bozza" && <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={() => setStato("in_corso")} data-testid="nis2-set-in-corso">Segna "In corso"</Button>}
                            {!rev.immutabile && rev.stato === "in_corso" && <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={() => setStato("bozza")} data-testid="nis2-set-bozza">Torna a "Bozza"</Button>}
                            {rev.immutabile && <Button size="sm" className="rounded-sm h-8 gap-1 text-xs" onClick={() => newRevisionFrom(rev.id)} data-testid="nis2-new-rev-from"><ArrowClockwise size={13} /> Crea nuova revisione</Button>}
                            {user?.role === "admin" && rev.stato !== "archiviata" && <Button variant="ghost" size="sm" className="rounded-sm h-8 gap-1 text-xs text-muted-foreground" onClick={() => setStato("archiviata")} data-testid="nis2-archive"><Archive size={13} /> Archivia</Button>}
                          </div>
                        </div>

                        {/* Stato salvataggio */}
                        <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-3" data-testid="nis2-save-status">
                          {dirty ? <span className="text-amber-600 font-medium flex items-center gap-1"><Clock size={12} /> Modifiche non salvate</span>
                                 : <span className="text-emerald-600 flex items-center gap-1"><CheckCircle size={12} /> Salvato</span>}
                          <span>Ultimo salvataggio: {fmtDT(lastSaved)}</span>
                          {rev.updated_by && <span>· da {rev.updated_by}</span>}
                        </div>

                        {rev.perimetro_version === 2 ? (
                          <PerimetroV2 schema={rev.perimetro_schema} perim={perim} onChange={setField} disabled={rev.immutabile} clientSedi={clientSedi} />
                        ) : (
                        <div className="space-y-4">
                          {Object.entries(groups).map(([gruppo, fields]) => (
                            <div key={gruppo}>
                              <div className="text-[11px] uppercase tracking-wider text-primary font-semibold mb-2">{gruppo}</div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {fields.map((f) => (
                                  <div key={f.key} className={["note_perimetro", "infrastrutture_incluse", "infrastrutture_escluse", "servizi_essenziali"].includes(f.key) ? "md:col-span-2" : ""}>
                                    <Label className="text-[11px] text-muted-foreground">{f.label}</Label>
                                    {["note_perimetro", "infrastrutture_incluse", "infrastrutture_escluse", "servizi_essenziali", "fornitori_critici", "sedi_incluse"].includes(f.key) ? (
                                      <Textarea value={perim[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} disabled={rev.immutabile} className="mt-1 rounded-sm text-sm min-h-[60px]" data-testid={`nis2-perim-${f.key}`} />
                                    ) : f.key === "data_assessment" ? (
                                      <DateField value={perim.data_assessment || ""} onChange={(v) => setField("data_assessment", v)} disabled={rev.immutabile} testid="nis2-perim-data_assessment" />
                                    ) : (
                                      <Input value={perim[f.key] || ""} onChange={(e) => setField(f.key, e.target.value)} disabled={rev.immutabile} className="mt-1 rounded-sm h-9 text-sm" data-testid={`nis2-perim-${f.key}`} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                          {/* Applicabilità NIS2 */}
                          <div>
                            <div className="text-[11px] uppercase tracking-wider text-primary font-semibold mb-2">Applicabilità NIS2</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <Label className="text-[11px] text-muted-foreground">Indicazione preliminare</Label>
                                <Input value={perim.applicabilita || ""} onChange={(e) => setField("applicabilita", e.target.value)} disabled={rev.immutabile} className="mt-1 rounded-sm h-9 text-sm" data-testid="nis2-perim-applicabilita" />
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-[11px] text-muted-foreground">Motivazione applicabilità / non applicabilità</Label>
                                <Textarea value={perim.motivazione_applicabilita || ""} onChange={(e) => setField("motivazione_applicabilita", e.target.value)} disabled={rev.immutabile} className="mt-1 rounded-sm text-sm min-h-[60px]" data-testid="nis2-perim-motivazione" />
                              </div>
                            </div>
                          </div>
                          {master?.disclaimer && <p className="text-[11px] text-muted-foreground border-t border-border pt-2 italic">{master.disclaimer}</p>}
                        </div>
                        )}

                        {!rev.immutabile && (
                          <div className="mt-4 flex justify-end">
                            <Button className="rounded-sm h-9 gap-1.5" onClick={savePerimetro} disabled={saving || !dirty} data-testid="nis2-save-perimetro"><FloppyDisk size={15} /> {saving ? "Salvataggio…" : "Salva perimetro"}</Button>
                          </div>
                        )}
                      </Card>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {tab === "questionario" && (
            !rev ? (
              <Card className="rounded-xl border-dashed border-slate-200 p-12 text-center text-sm text-muted-foreground" data-testid="nis2-questionario-noselection">
                Apri una revisione dalla scheda <b>Cliente e analisi</b> per compilare il questionario.
              </Card>
            ) : (
              <Questionario key={rev.id} rev={rev} master={master} focusNum={focusNum} flushRef={questFlushRef} onRevStato={(s) => setRev((r) => ({ ...r, stato: s, stato_label: s === "in_corso" ? "In corso" : r.stato_label, revisione_label: (s === "finalizzata" || s === "archiviata") ? r.revisione_label : (s === "in_corso" ? "In corso" : r.revisione_label) }))} reloadList={() => selClient && loadAssessments(selClient.id)} />
            )
          )}
          {tab === "risultati" && (
            !rev ? (
              <Card className="rounded-xl border-dashed border-slate-200 p-12 text-center text-sm text-muted-foreground" data-testid="nis2-risultati-noselection">
                Apri una revisione dalla scheda <b>Cliente e analisi</b> per vedere i risultati.
              </Card>
            ) : (
              <Risultati key={rev.id} rev={rev} onGoTo={(n) => { setFocusNum({ n, t: Date.now() }); setTab("questionario"); }} onFinalized={() => { openRevision(rev.id); if (selClient) loadAssessments(selClient.id); }} />
            )
          )}
        </div>

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) { setDelTarget(null); setDelText(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="nis2-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash size={18} /> Elimina analisi NIS2</DialogTitle>
            <DialogDescription>Operazione NON reversibile. Verranno eliminati in cascata revisioni, risposte, allegati e azioni correttive.</DialogDescription>
          </DialogHeader>
          {delTarget && (
            <div className="space-y-3 text-sm">
              <div className="rounded-sm border border-border p-3 space-y-1 text-xs">
                <div><span className="text-muted-foreground">Cliente:</span> <b>{delTarget.preview.cliente_nome || "—"}</b></div>
                <div><span className="text-muted-foreground">Analisi:</span> <b>{delTarget.preview.titolo}</b></div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  <span><span className="text-muted-foreground">Revisioni:</span> {delTarget.preview.num_revisioni}</span>
                  <span><span className="text-muted-foreground">Allegati:</span> {delTarget.preview.num_allegati}</span>
                  <span><span className="text-muted-foreground">Azioni:</span> {delTarget.preview.num_azioni}</span>
                  <span><span className="text-muted-foreground">Versioni:</span> {(delTarget.preview.versioni || []).map((v) => `v${v}`).join(", ") || "—"}</span>
                </div>
              </div>
              {!delTarget.preview.eliminabile ? (
                <p className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 p-2 text-xs" data-testid="nis2-delete-blocked">{delTarget.preview.motivo_blocco}</p>
              ) : (
                <div>
                  <Label className="text-xs">Per confermare digita <b>ELIMINA</b></Label>
                  <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="ELIMINA" className="mt-1 rounded-sm h-9" data-testid="nis2-delete-confirm-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setDelTarget(null); setDelText(""); }} data-testid="nis2-delete-cancel">Annulla</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" disabled={!delTarget?.preview?.eliminabile || delText.trim().toUpperCase() !== "ELIMINA" || delBusy} onClick={confirmDelete} data-testid="nis2-delete-confirm">{delBusy ? "Eliminazione…" : "Elimina definitivamente"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delRev} onOpenChange={(o) => { if (!o) { setDelRev(null); setDelRevText(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="nis2-delete-rev-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash size={18} /> Elimina revisione</DialogTitle>
            <DialogDescription>Operazione NON reversibile. Verranno eliminati in cascata risposte, allegati e azioni correttive di questa revisione. Le altre revisioni non vengono toccate.</DialogDescription>
          </DialogHeader>
          {delRev && (
            <div className="space-y-3 text-sm">
              <div className="rounded-sm border border-border p-3 space-y-1 text-xs">
                <div><span className="text-muted-foreground">Analisi:</span> <b>{delRev.titolo || "—"}</b></div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                  <span><span className="text-muted-foreground">Revisione:</span> {delRev.revisione_label}</span>
                  <span><span className="text-muted-foreground">Stato:</span> {delRev.stato_label}</span>
                  <span><span className="text-muted-foreground">Versione:</span> v{delRev.versione_questionario} · {delRev.num_domande} dom.</span>
                  <span><span className="text-muted-foreground">Allegati:</span> {delRev.num_allegati}</span>
                  <span><span className="text-muted-foreground">Azioni:</span> {delRev.num_azioni}</span>
                </div>
              </div>
              {!delRev.eliminabile ? (
                <p className="rounded-sm bg-amber-50 border border-amber-200 text-amber-800 p-2 text-xs" data-testid="nis2-delete-rev-blocked">{delRev.motivo_blocco}</p>
              ) : (
                <div>
                  <Label className="text-xs">Per confermare digita <b>ELIMINA</b></Label>
                  <Input value={delRevText} onChange={(e) => setDelRevText(e.target.value)} placeholder="ELIMINA" className="mt-1 rounded-sm h-9" data-testid="nis2-delete-rev-confirm-input" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => { setDelRev(null); setDelRevText(""); }} data-testid="nis2-delete-rev-cancel">Annulla</Button>
            <Button className="rounded-sm bg-red-600 hover:bg-red-700 text-white" disabled={!delRev?.eliminabile || delRevText.trim().toUpperCase() !== "ELIMINA" || delRevBusy} onClick={confirmDelRev} data-testid="nis2-delete-rev-confirm">{delRevBusy ? "Eliminazione…" : "Elimina revisione"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}

function Questionario({ rev, master, onRevStato, reloadList, focusNum, flushRef }) {
  const readOnly = rev.immutabile;
  const [answers, setAnswers] = useState(rev.answers || []);
  const [saveState, setSaveState] = useState("saved"); // saved | unsaved | saving | error
  const [lastSaved, setLastSaved] = useState(rev.updated_at);
  const [lastBy, setLastBy] = useState(rev.updated_by || "");
  const pendingRef = useRef({});
  const savingRef = useRef(false);
  const timerRef = useRef(null);
  const revStatoRef = useRef(rev.stato);

  const [search, setSearch] = useState("");
  const [fArea, setFArea] = useState("__all");
  const [fStato, setFStato] = useState("__all");
  const [fPrio, setFPrio] = useState("__all");
  const [fOwner, setFOwner] = useState("__all");
  const [onlyNC, setOnlyNC] = useState(false);
  const [onlyEM, setOnlyEM] = useState(false);
  const [fAtt, setFAtt] = useState("__all");
  const [fActions, setFActions] = useState("__all");
  const [expanded, setExpanded] = useState(() => new Set());

  const updateCounts = useCallback((aid, counts) => {
    setAnswers((arr) => arr.map((a) => (a.id === aid ? { ...a, ...counts } : a)));
  }, []);

  const areasOrder = useMemo(() => {
    const seen = []; (answers || []).forEach((a) => { if (a.area && !seen.includes(a.area)) seen.push(a.area); });
    return seen.length ? seen : (rev?.aree || master?.aree || []);
  }, [answers, rev, master]);

  const flush = useCallback(async (manual = false) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    const pend = pendingRef.current;
    const ids = Object.keys(pend);
    if (ids.length === 0) { if (manual) toast.info("Nessuna modifica da salvare"); return; }
    if (savingRef.current) return;
    const snapshot = ids.map((aid) => ({ answer_id: aid, ...pend[aid] }));
    pendingRef.current = {};
    savingRef.current = true;
    setSaveState("saving");
    try {
      const { data } = await api.post(`/nis2/revisions/${rev.id}/answers-batch`, { answers: snapshot });
      setLastSaved(data.updated_at); setLastBy(data.updated_by);
      if (data.stato && data.stato !== revStatoRef.current) { revStatoRef.current = data.stato; onRevStato?.(data.stato); reloadList?.(); }
      savingRef.current = false;
      if (Object.keys(pendingRef.current).length > 0) { setSaveState("unsaved"); timerRef.current = setTimeout(() => flush(), 3000); }
      else setSaveState("saved");
    } catch (e) {
      const cur = pendingRef.current;
      snapshot.forEach((s) => { const { answer_id, ...f } = s; cur[answer_id] = { ...f, ...(cur[answer_id] || {}) }; });
      pendingRef.current = cur;
      savingRef.current = false;
      setSaveState("error");
      toast.error("Errore nel salvataggio: i dati non sono andati persi, riprova.");
    }
  }, [rev.id, onRevStato, reloadList]);

  const change = (aid, field, value) => {
    if (readOnly) return;
    setAnswers((arr) => arr.map((a) => (a.id === aid ? { ...a, [field]: value } : a)));
    pendingRef.current[aid] = { ...(pendingRef.current[aid] || {}), [field]: value };
    setSaveState("unsaved");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(), 3000);
  };

  useEffect(() => {
    const h = (e) => { if (savingRef.current || saveState === "unsaved" || saveState === "error" || Object.keys(pendingRef.current).length) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [saveState]);
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  // Refetch delle risposte al mount (fix bug cambio scheda): evita di reinizializzare con dati obsoleti
  // contenuti in rev.answers. Le eventuali modifiche pendenti locali hanno la precedenza.
  useEffect(() => {
    let alive = true;
    api.get(`/nis2/revisions/${rev.id}`).then(({ data }) => {
      if (!alive || !Array.isArray(data.answers)) return;
      const pend = pendingRef.current;
      setAnswers(data.answers.map((a) => (pend[a.id] ? { ...a, ...pend[a.id] } : a)));
      setLastSaved(data.updated_at); setLastBy(data.updated_by || "");
    }).catch(() => {});
    return () => { alive = false; };
  }, [rev.id]);

  // Espone flush al parent per forzare il salvataggio prima del cambio scheda
  useEffect(() => {
    if (!flushRef) return undefined;
    flushRef.current = () => flush(false);
    return () => { if (flushRef) flushRef.current = null; };
  }, [flush, flushRef]);
  // Flush di sicurezza allo smontaggio (una sola volta): usa un ref alla versione più recente di flush
  const flushLatestRef = useRef(flush);
  useEffect(() => { flushLatestRef.current = flush; });
  useEffect(() => () => { flushLatestRef.current && flushLatestRef.current(false); }, []);

  useEffect(() => {
    if (!focusNum?.n) return;
    setExpanded(new Set(grouped.map((g) => g.area)));
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="nis2-q-item-${focusNum.n}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-primary", "rounded-lg"); setTimeout(() => el.classList.remove("ring-2", "ring-primary", "rounded-lg"), 2500); }
    }, 400);
    return () => clearTimeout(t);
  }, [focusNum]); // eslint-disable-line react-hooks/exhaustive-deps

  const isCompiled = (a) => !!a.stato;
  const grouped = useMemo(() => {
    const byArea = {};
    answers.forEach((a) => { (byArea[a.area] = byArea[a.area] || []).push(a); });
    const order = areasOrder.length ? areasOrder : Object.keys(byArea);
    return order.filter((ar) => byArea[ar]).map((ar) => ({ area: ar, items: byArea[ar].slice().sort((x, y) => x.ordine - y.ordine) }));
  }, [answers, areasOrder]);

  const matches = (a) => {
    if (fArea !== "__all" && a.area !== fArea) return false;
    if (fStato !== "__all" && (a.stato || "") !== (fStato === "__empty" ? "" : fStato)) return false;
    if (fPrio !== "__all" && a.priorita !== fPrio) return false;
    if (fOwner !== "__all" && a.owner !== fOwner) return false;
    if (onlyNC && a.stato) return false;
    if (onlyEM && !(a.evidenza_mancante || "").trim()) return false;
    if (fAtt === "with" && !(a.att_count > 0)) return false;
    if (fAtt === "without" && a.att_count > 0) return false;
    if (fActions === "with" && !(a.act_total > 0)) return false;
    if (fActions === "open" && !(a.act_open > 0)) return false;
    if (fActions === "overdue" && !(a.act_overdue > 0)) return false;
    if (fActions === "crithigh" && !(a.act_crit_high > 0)) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(`${a.numero}`.includes(q) || (a.domanda || "").toLowerCase().includes(q) || (a.note || "").toLowerCase().includes(q) || (a.evidenza_raccolta || "").toLowerCase().includes(q))) return false;
    }
    return true;
  };
  const filteredCount = useMemo(() => answers.filter(matches).length, [answers, search, fArea, fStato, fPrio, fOwner, onlyNC, onlyEM, fAtt, fActions]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const total = answers.length;
    const compiled = answers.filter(isCompiled).length;
    const senzaStato = answers.filter((a) => !a.stato).length;
    const nonVerif = answers.filter((a) => a.stato === "non_verificato").length;
    const naNoMot = answers.filter((a) => a.stato === "non_applicabile" && !(a.motivazione_na || "").trim()).length;
    const sum = (k) => answers.reduce((s, a) => s + (a[k] || 0), 0);
    return {
      total, compiled, senzaStato, nonVerif, naNoMot, pct: total ? Math.round(compiled / total * 100) : 0,
      attTotal: sum("att_count"), qWithAtt: answers.filter((a) => a.att_count > 0).length,
      actTotal: sum("act_total"), actOpen: sum("act_open"), actDone: sum("act_done"),
      actOverdue: sum("act_overdue"), actCritHigh: sum("act_crit_high"),
    };
  }, [answers]);

  const resetFilters = () => { setSearch(""); setFArea("__all"); setFStato("__all"); setFPrio("__all"); setFOwner("__all"); setOnlyNC(false); setOnlyEM(false); setFAtt("__all"); setFActions("__all"); };
  const expandAll = () => setExpanded(new Set(grouped.map((g) => g.area)));
  const collapseAll = () => setExpanded(new Set());
  const toggleArea = (ar) => setExpanded((s) => { const n = new Set(s); n.has(ar) ? n.delete(ar) : n.add(ar); return n; });
  const filtersActive = search || fArea !== "__all" || fStato !== "__all" || fPrio !== "__all" || fOwner !== "__all" || onlyNC || onlyEM || fAtt !== "__all" || fActions !== "__all";

  const SAVE_UI = {
    saved: { c: "text-emerald-600", i: <CheckCircle size={13} />, t: "Salvato" },
    unsaved: { c: "text-amber-600", i: <Clock size={13} />, t: "Modifiche non salvate" },
    saving: { c: "text-blue-600", i: <ArrowClockwise size={13} className="animate-spin" />, t: "Salvataggio in corso…" },
    error: { c: "text-red-600", i: <WarningCircle size={13} />, t: "Errore di salvataggio" },
  }[saveState];
  const fmtDT = (s) => { if (!s) return "—"; try { return new Date(s).toLocaleString("it-IT"); } catch { return s; } };

  return (
    <div className="space-y-4" data-testid="nis2-questionario">
      <Card className="rounded-xl border-slate-100 shadow-sm p-4 sticky top-14 z-10 bg-white/95 backdrop-blur">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h3 className="font-heading text-sm font-semibold">Questionario — {rev.revisione_label}</h3>
            <StatoBadge stato={rev.stato} label={rev.stato_label} />
            {readOnly && <span className="text-[11px] text-amber-600 font-medium" data-testid="nis2-q-readonly">Sola lettura</span>}
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${SAVE_UI.c}`} data-testid="nis2-q-savestate">{SAVE_UI.i} {SAVE_UI.t}</span>
            {saveState === "error" && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs gap-1" onClick={() => flush(true)} data-testid="nis2-q-retry"><ArrowClockwise size={12} /> Riprova</Button>}
            {!readOnly && <Button size="sm" className="h-8 rounded-sm text-xs gap-1" onClick={() => flush(true)} disabled={saveState === "saving"} data-testid="nis2-q-save"><FloppyDisk size={13} /> Salva</Button>}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <Progress value={totals.pct} className="h-2 flex-1" />
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="nis2-q-progress">{totals.compiled}/{totals.total} compilate · {totals.pct}%</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-muted-foreground">
          <span>Senza stato: <b className="text-foreground">{totals.senzaStato}</b></span>
          <span>Non verificate: <b className="text-foreground">{totals.nonVerif}</b></span>
          <span className={totals.naNoMot ? "text-red-600" : ""}>N/A senza motivazione: <b>{totals.naNoMot}</b></span>
          <span>Ultimo salvataggio: {fmtDT(lastSaved)}{lastBy ? ` · da ${lastBy}` : ""}</span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11px] text-muted-foreground border-t border-border/60 pt-2" data-testid="nis2-q-op-summary">
          <span className="inline-flex items-center gap-1"><Paperclip size={11} /> Allegati: <b className="text-foreground">{totals.attTotal}</b> ({totals.qWithAtt} domande)</span>
          <span className="inline-flex items-center gap-1"><ListChecks size={11} /> Azioni: <b className="text-foreground">{totals.actTotal}</b></span>
          <span>Aperte: <b className="text-foreground">{totals.actOpen}</b></span>
          <span>Completate: <b className="text-foreground">{totals.actDone}</b></span>
          <span className={totals.actOverdue ? "text-red-600" : ""}>Scadute: <b>{totals.actOverdue}</b></span>
          <span className={totals.actCritHigh ? "text-amber-600" : ""}>Critiche/Alte aperte: <b>{totals.actCritHigh}</b></span>
        </div>
      </Card>

      <Card className="rounded-xl border-slate-100 shadow-sm p-3" data-testid="nis2-q-filters">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nelle domande…" className="rounded-sm h-9 pl-8 w-56" data-testid="nis2-q-search" />
          </div>
          <Select value={fArea} onValueChange={setFArea}><SelectTrigger className="h-9 rounded-sm w-44 text-xs" data-testid="nis2-q-filter-area"><SelectValue placeholder="Area" /></SelectTrigger><SelectContent><SelectItem value="__all">Tutte le aree</SelectItem>{areasOrder.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          <Select value={fStato} onValueChange={setFStato}><SelectTrigger className="h-9 rounded-sm w-40 text-xs" data-testid="nis2-q-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger><SelectContent><SelectItem value="__all">Tutti gli stati</SelectItem><SelectItem value="__empty">Senza stato</SelectItem>{NIS2_STATI_RISPOSTA.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
          <Select value={fPrio} onValueChange={setFPrio}><SelectTrigger className="h-9 rounded-sm w-32 text-xs" data-testid="nis2-q-filter-prio"><SelectValue placeholder="Priorità" /></SelectTrigger><SelectContent><SelectItem value="__all">Tutte le priorità</SelectItem>{NIS2_PRIORITA.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
          <Select value={fOwner} onValueChange={setFOwner}><SelectTrigger className="h-9 rounded-sm w-36 text-xs" data-testid="nis2-q-filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger><SelectContent><SelectItem value="__all">Tutti gli owner</SelectItem>{NIS2_OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="nis2-q-filter-notcompiled"><Checkbox checked={onlyNC} onCheckedChange={(v) => setOnlyNC(!!v)} /> Non compilate</label>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid="nis2-q-filter-evmissing"><Checkbox checked={onlyEM} onCheckedChange={(v) => setOnlyEM(!!v)} /> Evidenza mancante</label>
          <Select value={fAtt} onValueChange={setFAtt}><SelectTrigger className="h-9 rounded-sm w-36 text-xs" data-testid="nis2-q-filter-att"><SelectValue placeholder="Allegati" /></SelectTrigger><SelectContent><SelectItem value="__all">Allegati: tutti</SelectItem><SelectItem value="with">Con allegati</SelectItem><SelectItem value="without">Senza allegati</SelectItem></SelectContent></Select>
          <Select value={fActions} onValueChange={setFActions}><SelectTrigger className="h-9 rounded-sm w-40 text-xs" data-testid="nis2-q-filter-actions"><SelectValue placeholder="Azioni" /></SelectTrigger><SelectContent><SelectItem value="__all">Azioni: tutte</SelectItem><SelectItem value="with">Con azioni</SelectItem><SelectItem value="open">Con azioni aperte</SelectItem><SelectItem value="overdue">Con azioni scadute</SelectItem><SelectItem value="crithigh">Critiche/Alte aperte</SelectItem></SelectContent></Select>
          {filtersActive && <Button size="sm" variant="ghost" className="h-8 rounded-sm text-xs gap-1" onClick={resetFilters} data-testid="nis2-q-reset-filters"><X size={12} /> Azzera</Button>}
          <span className="text-xs text-muted-foreground ml-auto" data-testid="nis2-q-result-count">{filteredCount} risultati</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs gap-1" onClick={expandAll} data-testid="nis2-q-expand-all"><ArrowsOut size={12} /> Espandi tutte</Button>
          <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs gap-1" onClick={collapseAll} data-testid="nis2-q-collapse-all"><ArrowsIn size={12} /> Comprimi tutte</Button>
        </div>
      </Card>

      {grouped.map((g) => {
        const items = g.items.filter(matches);
        if (items.length === 0) return null;
        const total = g.items.length;
        const comp = g.items.filter(isCompiled).length;
        const pct = total ? Math.round(comp / total * 100) : 0;
        const open = expanded.has(g.area);
        return (
          <Card key={g.area} className="rounded-xl border-slate-100 shadow-sm overflow-hidden" data-testid={`nis2-q-area`}>
            <button className="w-full flex items-center justify-between gap-3 p-3.5 hover:bg-secondary/40 transition-colors" onClick={() => toggleArea(g.area)} data-testid={`nis2-q-area-toggle`}>
              <span className="flex items-center gap-2 font-semibold text-sm">{open ? <CaretDown size={14} /> : <CaretRight size={14} />} {g.area}</span>
              <span className="flex items-center gap-3 text-[11px] text-muted-foreground">
                <span>{comp}/{total} compilate</span>
                <span>{total - comp} da compilare</span>
                <Progress value={pct} className="h-1.5 w-24" />
                <span className="font-medium text-foreground">{pct}%</span>
              </span>
            </button>
            {open && (
              <div className="border-t border-border divide-y divide-border">
                {items.map((a) => {
                  const naMissing = a.stato === "non_applicabile" && !(a.motivazione_na || "").trim();
                  return (
                    <div key={a.id} className="p-4 space-y-3" data-testid={`nis2-q-item-${a.numero}`}>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-0.5">
                          <span className="font-mono font-semibold text-primary">#{a.numero}</span>
                          {a.lettera && a.lettera !== "-" && <span className="rounded bg-secondary px-1.5 py-0.5">punto {a.lettera}</span>}
                          {a.rif_acn && <span className="rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 font-mono" data-testid={`nis2-q-rif-${a.numero}`}>{a.rif_acn}</span>}
                          {a.critico && <span className="rounded bg-red-100 text-red-700 px-1.5 py-0.5 font-semibold inline-flex items-center gap-0.5" data-testid={`nis2-q-critico-${a.numero}`}><WarningOctagon size={11} /> CRITICO</span>}
                          <span>{a.area}</span>
                          {a.stato && <span className={NIS2_STATI_RISPOSTA.find((s) => s.v === a.stato)?.c}>● {NIS2_STATO_RISP_LABEL[a.stato]}</span>}
                          {a.att_count > 0 && <span className="inline-flex items-center gap-0.5" data-testid={`nis2-q-ind-att-${a.numero}`}><Paperclip size={11} /> {a.att_count}</span>}
                          {a.act_open > 0 && <span className="inline-flex items-center gap-0.5 text-blue-600" data-testid={`nis2-q-ind-actopen-${a.numero}`}><ListChecks size={11} /> {a.act_open}</span>}
                          {a.act_done > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-600"><CheckCircle size={11} /> {a.act_done}</span>}
                          {a.act_overdue > 0 && <span className="inline-flex items-center gap-0.5 text-red-600" data-testid={`nis2-q-ind-overdue-${a.numero}`}><WarningOctagon size={11} /> {a.act_overdue}</span>}
                          {a.act_crit_high > 0 && <span className="inline-flex items-center gap-0.5 text-amber-600"><WarningCircle size={11} /> {a.act_crit_high}</span>}
                        </div>
                        <p className="text-sm font-medium">{a.domanda}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                        <div><span className="font-semibold text-foreground/70">Dove verificare:</span> {a.dove_verificare || "—"}</div>
                        <div><span className="font-semibold text-foreground/70">Evidenza richiesta:</span> {a.evidenza_richiesta || "—"}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[11px] text-muted-foreground">Stato</Label>
                          <Select value={a.stato || "__none"} onValueChange={(v) => change(a.id, "stato", v === "__none" ? "" : v)} disabled={readOnly}>
                            <SelectTrigger className="h-9 rounded-sm text-sm mt-1" data-testid={`nis2-q-stato-${a.numero}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="__none">— Seleziona —</SelectItem>{NIS2_STATI_RISPOSTA.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-muted-foreground">Priorità</Label>
                          <Select value={a.priorita || "media"} onValueChange={(v) => change(a.id, "priorita", v)} disabled={readOnly}>
                            <SelectTrigger className="h-9 rounded-sm text-sm mt-1" data-testid={`nis2-q-prio-${a.numero}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{NIS2_PRIORITA.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[11px] text-muted-foreground">Owner</Label>
                          <Select value={a.owner || "Tecnologie"} onValueChange={(v) => change(a.id, "owner", v)} disabled={readOnly}>
                            <SelectTrigger className="h-9 rounded-sm text-sm mt-1" data-testid={`nis2-q-owner-${a.numero}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{NIS2_OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      {a.stato === "non_applicabile" && (
                        <div>
                          <Label className={`text-[11px] ${naMissing ? "text-red-600 font-medium" : "text-muted-foreground"}`}>Motivazione {naMissing && "(obbligatoria)"}</Label>
                          <Textarea value={a.motivazione_na || ""} onChange={(e) => change(a.id, "motivazione_na", e.target.value)} disabled={readOnly} className={`mt-1 rounded-sm text-sm min-h-[50px] ${naMissing ? "border-red-300" : ""}`} data-testid={`nis2-q-mot-${a.numero}`} />
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div><Label className="text-[11px] text-muted-foreground">Evidenza raccolta</Label><Textarea value={a.evidenza_raccolta || ""} onChange={(e) => change(a.id, "evidenza_raccolta", e.target.value)} disabled={readOnly} className="mt-1 rounded-sm text-sm min-h-[50px]" data-testid={`nis2-q-evracc-${a.numero}`} /></div>
                        <div><Label className="text-[11px] text-muted-foreground">Evidenza mancante</Label><Textarea value={a.evidenza_mancante || ""} onChange={(e) => change(a.id, "evidenza_mancante", e.target.value)} disabled={readOnly} className="mt-1 rounded-sm text-sm min-h-[50px]" data-testid={`nis2-q-evmanc-${a.numero}`} /></div>
                      </div>
                      <div><Label className="text-[11px] text-muted-foreground">Note</Label><Textarea value={a.note || ""} onChange={(e) => change(a.id, "note", e.target.value)} disabled={readOnly} className="mt-1 rounded-sm text-sm min-h-[50px]" data-testid={`nis2-q-note-${a.numero}`} /></div>
                      <QuestionExtras answer={a} readOnly={readOnly} onCounts={updateCounts} suggestAction={a.stato === "non_conforme" || a.stato === "parzialmente_conforme"} beforeUpload={() => flush(true)} />
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function fmtSize(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function AttRow({ att, readOnly, onDelete, onSave, busy }) {
  const [desc, setDesc] = useState(att.descrizione || "");
  const [url, setUrl] = useState(att.url_esterno || "");
  const dirty = desc !== (att.descrizione || "") || url !== (att.url_esterno || "");
  const dl = () => { window.open(`${process.env.REACT_APP_BACKEND_URL}/api${att.download_url}`, "_blank"); };
  return (
    <div className="border border-border rounded-md p-2.5 space-y-2" data-testid={`nis2-att-row-${att.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip size={13} className="text-primary shrink-0" />
          <span className="text-sm font-medium truncate">{att.filename || "Link esterno"}</span>
          {att.size ? <span className="text-[11px] text-muted-foreground shrink-0">{fmtSize(att.size)}</span> : null}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {att.object_key !== null && att.filename && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={dl} title="Scarica" data-testid={`nis2-att-dl-${att.id}`}><DownloadSimple size={14} /></Button>}
          {!readOnly && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(att.id)} disabled={busy} title="Elimina" data-testid={`nis2-att-del-${att.id}`}><Trash size={14} /></Button>}
        </div>
      </div>
      <div className="text-[11px] text-muted-foreground">Caricato da {att.uploaded_by || "—"} · {att.created_at ? new Date(att.created_at).toLocaleString("it-IT") : "—"}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Input value={desc} onChange={(e) => setDesc(e.target.value)} disabled={readOnly} placeholder="Descrizione" className="h-8 rounded-sm text-xs" data-testid={`nis2-att-desc-${att.id}`} />
        <Input value={url} onChange={(e) => setUrl(e.target.value)} disabled={readOnly} placeholder="URL esterno (facoltativo)" className="h-8 rounded-sm text-xs" data-testid={`nis2-att-url-${att.id}`} />
      </div>
      {!readOnly && dirty && <Button size="sm" className="h-7 rounded-sm text-xs" onClick={() => onSave(att.id, desc, url)} data-testid={`nis2-att-save-${att.id}`}>Salva metadati</Button>}
    </div>
  );
}

function AddActionDialog({ open, onClose, onCreate, busy }) {
  const EMPTY = { titolo: "", descrizione: "", priorita: "media", complessita: "media", tempo_stimato: "", deliverable: "", figura: "", responsabile: "", scadenza: "" };
  const [mode, setMode] = useState("catalogo");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("__all");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });

  useEffect(() => {
    if (!open) return;
    setMode("catalogo"); setQ(""); setCat("__all"); setSel(null); setForm({ ...EMPTY });
  }, [open]);

  const loadCat = useCallback(async () => {
    if (!open || mode !== "catalogo") return;
    setLoading(true);
    try {
      const params = { attiva: "true" };
      if (q) params.q = q;
      if (cat !== "__all") params.categoria = cat;
      const { data } = await api.get("/activity-catalog", { params });
      setItems(data.items || []); setCats(data.categorie || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [open, mode, q, cat]);
  useEffect(() => { const t = setTimeout(loadCat, 250); return () => clearTimeout(t); }, [loadCat]);

  const pick = (it) => {
    setSel(it);
    const prio = NIS2_PRIORITA.some((p) => p.v === it.priorita_suggerita) ? it.priorita_suggerita : "media";
    setForm({
      titolo: it.titolo || "", descrizione: it.descrizione || "",
      priorita: prio, complessita: it.complessita || "media",
      tempo_stimato: it.tempo_medio_ore ?? "", deliverable: it.deliverable || "",
      figura: it.figura_professionale || "", responsabile: "", scadenza: "",
    });
  };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = () => {
    const titolo = (form.titolo || "").trim() || (mode === "manuale" ? "Nuova azione" : "");
    if (!titolo) { toast.error("Seleziona un'attività o inserisci un titolo"); return; }
    const fromCatalog = mode === "catalogo" && !!sel;
    onCreate({
      titolo, descrizione: form.descrizione || "", responsabile: form.responsabile || "",
      scadenza: form.scadenza || "", priorita: form.priorita || "media", stato: "da_pianificare",
      origine: fromCatalog ? "catalogo" : "manuale", catalog_id: fromCatalog ? sel.id : "",
      complessita: form.complessita || "", deliverable: form.deliverable || "", figura: form.figura || "",
      tempo_stimato: form.tempo_stimato === "" || form.tempo_stimato == null ? null : Number(form.tempo_stimato),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="nis2-addaction-dialog">
        <DialogHeader>
          <DialogTitle>Aggiungi azione correttiva</DialogTitle>
          <DialogDescription>Scegli un'attività dal catalogo standard oppure inseriscila manualmente.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Button type="button" variant={mode === "catalogo" ? "default" : "outline"} className="rounded-sm flex-1 gap-1 h-9" onClick={() => setMode("catalogo")} data-testid="nis2-addaction-mode-catalogo"><ClipboardText size={15} /> Attività da Catalogo</Button>
          <Button type="button" variant={mode === "manuale" ? "default" : "outline"} className="rounded-sm flex-1 gap-1 h-9" onClick={() => { setMode("manuale"); setSel(null); setForm({ ...EMPTY }); }} data-testid="nis2-addaction-mode-manuale"><PencilSimple size={15} /> Attività Manuale</Button>
        </div>

        {mode === "catalogo" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per titolo, codice, categoria, tag…" className="pl-8 rounded-sm h-9" data-testid="nis2-addaction-search" />
              </div>
              <Select value={cat} onValueChange={setCat}><SelectTrigger className="h-9 rounded-sm w-48 text-sm" data-testid="nis2-addaction-cat-filter"><SelectValue placeholder="Categoria" /></SelectTrigger><SelectContent><SelectItem value="__all">Tutte le categorie</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="border rounded-md max-h-52 overflow-y-auto divide-y" data-testid="nis2-addaction-catalog-list">
              {loading && <div className="p-3 text-xs text-muted-foreground">Caricamento…</div>}
              {!loading && items.length === 0 && <div className="p-3 text-xs text-muted-foreground">Nessuna attività trovata.</div>}
              {items.map((it) => (
                <button type="button" key={it.id} onClick={() => pick(it)} className={`w-full text-left p-2.5 hover:bg-secondary/60 transition-colors ${sel?.id === it.id ? "bg-primary/5" : ""}`} data-testid={`nis2-addaction-cat-${it.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] bg-secondary px-1.5 py-0.5 rounded">{it.codice}</span>
                    <span className="font-medium text-sm">{it.titolo}</span>
                    <span className="text-[10px] text-muted-foreground">{it.categoria}</span>
                    <span className="text-[10px] text-muted-foreground">· ~{it.tempo_medio_ore ?? "?"}h · compl. {it.complessita}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{it.descrizione}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {(sel || mode === "manuale") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm border-t pt-3">
            {mode === "catalogo" && sel && <p className="md:col-span-2 text-[11px] text-primary">Compilato dal catalogo <span className="font-mono">{sel.codice}</span> — tutti i campi restano modificabili.</p>}
            <div className="md:col-span-2"><Label className="text-xs">Titolo *</Label><Input value={form.titolo} onChange={(e) => setF("titolo", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="nis2-addaction-titolo" /></div>
            <div className="md:col-span-2"><Label className="text-xs">Descrizione</Label><Textarea value={form.descrizione} onChange={(e) => setF("descrizione", e.target.value)} className="mt-1 rounded-sm min-h-[60px]" data-testid="nis2-addaction-descrizione" /></div>
            <div><Label className="text-xs">Priorità</Label><Select value={form.priorita} onValueChange={(v) => setF("priorita", v)}><SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="nis2-addaction-priorita"><SelectValue /></SelectTrigger><SelectContent>{NIS2_PRIORITA.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Complessità</Label><Select value={form.complessita || "media"} onValueChange={(v) => setF("complessita", v)}><SelectTrigger className="mt-1 h-9 rounded-sm"><SelectValue /></SelectTrigger><SelectContent>{["bassa", "media", "alta"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Tempo stimato (ore)</Label><Input type="number" value={form.tempo_stimato} onChange={(e) => setF("tempo_stimato", e.target.value)} className="mt-1 rounded-sm h-9" data-testid="nis2-addaction-tempo" /></div>
            <div><Label className="text-xs">Deliverable</Label><Input value={form.deliverable} onChange={(e) => setF("deliverable", e.target.value)} className="mt-1 rounded-sm h-9" /></div>
            <div><Label className="text-xs">Responsabile</Label><Input list="nis2-addaction-owners" value={form.responsabile} onChange={(e) => setF("responsabile", e.target.value)} className="mt-1 rounded-sm h-9" /><datalist id="nis2-addaction-owners">{NIS2_OWNERS.map((o) => <option key={o} value={o} />)}</datalist></div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Annulla</Button>
          <Button className="rounded-sm" disabled={busy} onClick={submit} data-testid="nis2-addaction-save">{busy ? "Salvataggio…" : "Aggiungi azione"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActionRow({ act, readOnly, onPatch, onDelete }) {
  const overdue = act.scadenza && act.scadenza < nis2Today() && !["completata", "annullata"].includes(act.stato);
  return (
    <div className={`border rounded-md p-2.5 space-y-2 ${overdue ? "border-red-300 bg-red-50/40" : "border-border"}`} data-testid={`nis2-action-row-${act.id}`}>
      <div className="flex items-center gap-2">
        <Input defaultValue={act.titolo} onBlur={(e) => e.target.value !== act.titolo && onPatch(act.id, { titolo: e.target.value })} disabled={readOnly} placeholder="Titolo azione" className="h-8 rounded-sm text-sm font-medium flex-1" data-testid={`nis2-action-titolo-${act.id}`} />
        {overdue && <span className="text-[11px] text-red-600 font-medium inline-flex items-center gap-1"><WarningOctagon size={12} /> Scaduta</span>}
        {!readOnly && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDelete(act.id)} title="Elimina" data-testid={`nis2-action-del-${act.id}`}><Trash size={14} /></Button>}
      </div>
      <Textarea defaultValue={act.descrizione} onBlur={(e) => e.target.value !== (act.descrizione || "") && onPatch(act.id, { descrizione: e.target.value })} disabled={readOnly} placeholder="Descrizione" className="rounded-sm text-xs min-h-[40px]" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Responsabile</Label>
          <Input list={`resp-${act.id}`} defaultValue={act.responsabile} onBlur={(e) => e.target.value !== (act.responsabile || "") && onPatch(act.id, { responsabile: e.target.value })} disabled={readOnly} className="h-8 rounded-sm text-xs mt-0.5" data-testid={`nis2-action-resp-${act.id}`} />
          <datalist id={`resp-${act.id}`}>{NIS2_OWNERS.map((o) => <option key={o} value={o} />)}</datalist>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Scadenza</Label>
          <DateField value={act.scadenza || ""} onChange={(v) => onPatch(act.id, { scadenza: v })} disabled={readOnly} testid={`nis2-action-scad-${act.id}`} />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Stato</Label>
          <Select value={act.stato} onValueChange={(v) => onPatch(act.id, { stato: v })} disabled={readOnly}><SelectTrigger className="h-8 rounded-sm text-xs mt-0.5" data-testid={`nis2-action-stato-${act.id}`}><SelectValue /></SelectTrigger><SelectContent>{NIS2_ACTION_STATI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent></Select>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Priorità</Label>
          <Select value={act.priorita} onValueChange={(v) => onPatch(act.id, { priorita: v })} disabled={readOnly}><SelectTrigger className="h-8 rounded-sm text-xs mt-0.5" data-testid={`nis2-action-prio-${act.id}`}><SelectValue /></SelectTrigger><SelectContent>{NIS2_PRIORITA.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>
      {act.data_completamento && <div className="text-[11px] text-emerald-600">Completata il {act.data_completamento}</div>}
      {(act.origine === "catalogo" || act.complessita || act.tempo_stimato != null || act.deliverable) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground pt-0.5 border-t border-border/50 mt-1">
          {act.origine === "catalogo" && <span className="inline-flex items-center gap-1 text-primary"><ClipboardText size={11} /> Da catalogo{act.catalog_id ? "" : ""}</span>}
          {act.complessita && <span>· compl. {act.complessita}</span>}
          {act.tempo_stimato != null && <span>· ~{act.tempo_stimato}h</span>}
          {act.figura && <span>· {act.figura}</span>}
          {act.deliverable && <span>· 📄 {act.deliverable}</span>}
        </div>
      )}
    </div>
  );
}

function QuestionExtras({ answer, readOnly, onCounts, suggestAction, beforeUpload }) {
  const [atts, setAtts] = useState(null);
  const [actions, setActions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const fileRef = useRef(null);
  const answerId = answer.id;

  const recount = useCallback((aList, acList) => {
    const today = nis2Today();
    const open = acList.filter((x) => !["completata", "annullata"].includes(x.stato));
    onCounts?.(answerId, {
      att_count: aList.length, act_total: acList.length, act_open: open.length,
      act_done: acList.filter((x) => x.stato === "completata").length,
      act_overdue: open.filter((x) => x.scadenza && x.scadenza < today).length,
      act_crit_high: open.filter((x) => ["critica", "alta"].includes(x.priorita)).length,
    });
  }, [answerId, onCounts]);

  const load = useCallback(async () => {
    try {
      const [ra, rc] = await Promise.all([
        api.get(`/nis2/answers/${answerId}/attachments`),
        api.get(`/nis2/answers/${answerId}/actions`),
      ]);
      setAtts(ra.data); setActions(rc.data); recount(ra.data, rc.data);
    } catch { setAtts([]); setActions([]); }
  }, [answerId, recount]);
  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file || readOnly || busy) return;
    setBusy(true);
    try {
      await beforeUpload?.();
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/nis2/answers/${answerId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const na = [...(atts || []), data]; setAtts(na); recount(na, actions || []);
      toast.success("Allegato caricato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Caricamento fallito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addLink = async () => {
    if (!linkUrl.trim() || readOnly || busy) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("url_esterno", linkUrl.trim());
      const { data } = await api.post(`/nis2/answers/${answerId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const na = [...(atts || []), data]; setAtts(na); recount(na, actions || []); setLinkUrl("");
      toast.success("Link aggiunto");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const delAtt = async (id) => {
    if (readOnly || busy) return;
    if (!window.confirm("Eliminare questo allegato?")) return;
    setBusy(true);
    try { await api.delete(`/nis2/attachments/${id}`); const na = (atts || []).filter((x) => x.id !== id); setAtts(na); recount(na, actions || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  const saveAttMeta = async (id, descrizione, url_esterno) => {
    try { const { data } = await api.patch(`/nis2/attachments/${id}`, { descrizione, url_esterno }); setAtts((arr) => arr.map((x) => x.id === id ? data : x)); toast.success("Allegato aggiornato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const addAction = async (payload) => {
    if (readOnly || busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/nis2/answers/${answerId}/actions`, payload); const na = [...(actions || []), data]; setActions(na); recount(atts || [], na); setAddOpen(false); toast.success("Azione aggiunta"); }
    catch (e) { toast.error("Errore"); } finally { setBusy(false); }
  };
  const patchAction = async (id, fields) => {
    setActions((arr) => arr.map((x) => x.id === id ? { ...x, ...fields } : x));
    try { const { data } = await api.patch(`/nis2/actions/${id}`, fields); setActions((arr) => { const na = arr.map((x) => x.id === id ? data : x); recount(atts || [], na); return na; }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); load(); }
  };
  const delAction = async (id) => {
    if (!window.confirm("Eliminare questa azione?")) return;
    try { await api.delete(`/nis2/actions/${id}`); const na = (actions || []).filter((x) => x.id !== id); setActions(na); recount(atts || [], na); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="pt-2 border-t border-border/60 space-y-4" data-testid={`nis2-extras-${answer.numero}`}>
      {/* Allegati */}
      <div>
        <div className="flex items-center gap-2 mb-2"><Paperclip size={14} className="text-primary" /><span className="text-xs font-semibold">Allegati ed evidenze {atts ? `(${atts.length})` : ""}</span></div>
        {!readOnly && (
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files?.[0]) doUpload(e.dataTransfer.files[0]); }}
            className={`rounded-md border border-dashed p-3 text-center transition-colors ${drag ? "border-primary bg-primary/5" : "border-slate-200"}`}
            data-testid={`nis2-att-dropzone-${answer.numero}`}>
            <input type="file" ref={fileRef} className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} data-testid={`nis2-att-input-${answer.numero}`} />
            <p className="text-[11px] text-muted-foreground">Trascina un file qui oppure</p>
            <div className="flex items-center justify-center gap-2 mt-1.5">
              <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs gap-1" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`nis2-att-add-${answer.numero}`}><UploadSimple size={13} /> {busy ? "Caricamento…" : "Aggiungi allegato"}</Button>
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <LinkSimple size={13} className="text-muted-foreground" />
              <Input value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://… (link esterno)" className="h-7 rounded-sm text-xs w-64" data-testid={`nis2-att-linkinput-${answer.numero}`} />
              <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs" onClick={addLink} disabled={busy || !linkUrl.trim()} data-testid={`nis2-att-addlink-${answer.numero}`}>Aggiungi link</Button>
            </div>
          </div>
        )}
        {atts && atts.length > 0 && <div className="space-y-2 mt-2">{atts.map((at) => <AttRow key={at.id} att={at} readOnly={readOnly} onDelete={delAtt} onSave={saveAttMeta} busy={busy} />)}</div>}
        {atts && atts.length === 0 && readOnly && <p className="text-[11px] text-muted-foreground">Nessun allegato</p>}
      </div>

      {/* Azioni correttive */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2"><ListChecks size={14} className="text-primary" /><span className="text-xs font-semibold">Azioni correttive {actions ? `(${actions.length})` : ""}</span></div>
          {!readOnly && <Button size="sm" variant="outline" className="h-7 rounded-sm text-xs gap-1" onClick={() => setAddOpen(true)} disabled={busy} data-testid={`nis2-action-add-${answer.numero}`}><Plus size={13} /> Aggiungi azione</Button>}
        </div>
        {suggestAction && (!actions || actions.length === 0) && (
          <p className="text-[11px] text-amber-600 mb-2 inline-flex items-center gap-1" data-testid={`nis2-action-suggest-${answer.numero}`}><WarningCircle size={12} /> Suggerito: crea almeno un'azione correttiva per questa domanda.</p>
        )}
        {actions && actions.length > 0 && <div className="space-y-2">{actions.map((ac) => <ActionRow key={ac.id} act={ac} readOnly={readOnly} onPatch={patchAction} onDelete={delAction} />)}</div>}
        {actions && actions.length === 0 && !suggestAction && <p className="text-[11px] text-muted-foreground">Nessuna azione correttiva</p>}
      </div>
      <AddActionDialog open={addOpen} onClose={() => setAddOpen(false)} onCreate={addAction} busy={busy} />
    </div>
  );
}

const NIS2_STATO_PIE_COLORS = { conforme: "#16a34a", parzialmente_conforme: "#d97706", non_conforme: "#dc2626", non_verificato: "#94a3b8" };
const NIS2_PRIO_LABEL = { critica: "Critica", alta: "Alta", media: "Media", bassa: "Bassa" };

function KpiBox({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white p-3 text-center">
      <div className={`text-2xl font-bold ${accent || "text-foreground"}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function Delta({ v, suffix = "" }) {
  if (v === null || v === undefined) return <span className="text-muted-foreground">—</span>;
  const up = v > 0, down = v < 0;
  return <span className={up ? "text-emerald-600" : down ? "text-red-600" : "text-muted-foreground"}>{up ? "▲" : down ? "▼" : "="} {v > 0 ? "+" : ""}{v}{suffix}</span>;
}

function Risultati({ rev, onGoTo, onFinalized }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [val, setVal] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [conf, setConf] = useState(false);
  const [busy, setBusy] = useState(false);
  const immutabile = rev.stato === "finalizzata" || rev.stato === "archiviata";
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/nis2/revisions/${rev.id}/results`).then(({ data }) => { if (alive) { setData(data); setLoading(false); } }).catch(() => { if (alive) { setData(null); setLoading(false); } });
    if (immutabile) api.get(`/nis2/revisions/${rev.id}/report`).then(({ data }) => { if (alive) setReport(data); }).catch(() => {});
    return () => { alive = false; };
  }, [rev.id, immutabile]);

  const openFinalize = async () => {
    setBusy(true); setConf(false);
    try {
      const { data } = await api.post(`/nis2/revisions/${rev.id}/validate-finalization`);
      setVal(data); setDlgOpen(true);
    } catch { toast.error("Errore nella validazione"); } finally { setBusy(false); }
  };
  const doFinalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/nis2/revisions/${rev.id}/finalize`, { conferma_avvisi: true });
      toast.success(`Analisi finalizzata come ${data.revisione_label} e report generato`);
      setDlgOpen(false);
      onFinalized && onFinalized();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? (d.messaggio || "Finalizzazione bloccata") : (d || "Errore"));
    } finally { setBusy(false); }
  };
  const regenerate = async () => {
    setBusy(true);
    try { await api.post(`/nis2/revisions/${rev.id}/report/regenerate`); const { data } = await api.get(`/nis2/revisions/${rev.id}/report`); setReport(data); toast.success("PDF rigenerato dallo snapshot"); }
    catch { toast.error("Errore nella rigenerazione"); } finally { setBusy(false); }
  };
  const archive = async () => {
    if (!window.confirm("Archiviare questa revisione finalizzata? Resterà in sola lettura e conservata nello storico.")) return;
    setBusy(true);
    try { await api.patch(`/nis2/revisions/${rev.id}/stato`, { stato: "archiviata" }); toast.success("Revisione archiviata"); onFinalized && onFinalized(); }
    catch { toast.error("Errore nell'archiviazione"); } finally { setBusy(false); }
  };
  const openReport = async (inline) => {
    if (!report) return;
    try {
      const { data } = await api.get(`/nis2/reports/${report.id}/${inline ? "view" : "download"}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      if (inline) { window.open(url, "_blank"); }
      else { const a = document.createElement("a"); a.href = url; a.download = report.filename || "report.pdf"; a.click(); }
    } catch { toast.error("Impossibile aprire il PDF"); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-32 rounded-xl" /><Skeleton className="h-64 rounded-xl" /></div>;
  if (!data) return <Card className="p-8 text-center text-sm text-muted-foreground">Impossibile calcolare i risultati.</Card>;

  const ex = data.executive; const sc = data.scoring; const sem = ex.semaforo;
  const pieData = Object.entries(sc.stato_counts).filter(([k]) => NIS2_STATO_PIE_COLORS[k]).map(([k, v]) => ({ name: NIS2_STATO_RISP_LABEL[k] || k, key: k, value: v })).filter((d) => d.value > 0);
  const areaData = sc.per_area.filter((a) => a.pct !== null).map((a) => ({ name: a.nome, pct: a.pct, domande: a.domande }));
  const prioData = sc.per_priorita.map((p) => ({ name: NIS2_PRIO_LABEL[p.nome], pct: p.pct || 0, domande: p.domande }));
  const azData = [
    { name: "Aperte", value: sc.azioni.aperte, fill: "#2563eb" },
    { name: "Completate", value: sc.azioni.completate, fill: "#16a34a" },
    { name: "Scadute", value: sc.azioni.scadute, fill: "#dc2626" },
    { name: "Annullate", value: sc.azioni.annullate, fill: "#94a3b8" },
  ];

  return (
    <div className="space-y-5" data-testid="nis2-risultati">
      {/* Finalizzazione / Report */}
      {immutabile ? (
        <Card className="rounded-xl border-emerald-100 bg-emerald-50/40 shadow-sm p-4" data-testid="nis2-report-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              <div>
                <div className="text-sm font-semibold">{rev.stato === "archiviata" ? "Revisione archiviata" : "Analisi finalizzata"} — {rev.revisione_label}</div>
                <div className="text-xs text-muted-foreground">Sola lettura · report PDF disponibile{report ? ` · ${report.filename}` : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" disabled={!report} onClick={() => openReport(true)} data-testid="nis2-report-view"><FileText size={14} /> Apri PDF</Button>
              <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" disabled={!report} onClick={() => openReport(false)} data-testid="nis2-report-download"><DownloadSimple size={14} /> Scarica</Button>
              <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" disabled={busy} onClick={regenerate} data-testid="nis2-report-regenerate"><ArrowClockwise size={14} /> Rigenera PDF</Button>
              {rev.stato === "finalizzata" && <Button variant="outline" size="sm" className="rounded-sm h-8 gap-1 text-xs" disabled={busy} onClick={archive} data-testid="nis2-archive"><Archive size={14} /> Archivia</Button>}
            </div>
          </div>
        </Card>
      ) : (
        <Card className="rounded-xl border-slate-100 shadow-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid="nis2-finalize-panel">
          <div className="text-sm text-muted-foreground">Al termine della compilazione puoi finalizzare l'analisi: verrà assegnata una revisione <b>REV.xx</b>, creato uno snapshot immutabile e generato il report PDF.</div>
          <Button className="rounded-sm h-9 gap-2 bg-primary" disabled={busy} onClick={openFinalize} data-testid="nis2-finalize-btn"><CheckCircle size={16} /> Finalizza analisi e genera report</Button>
        </Card>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="nis2-finalize-dialog">
          <DialogHeader>
            <DialogTitle>Finalizzazione analisi NIS2</DialogTitle>
            <DialogDescription>Verifica il riepilogo prima di finalizzare. L'operazione crea uno snapshot immutabile e genera il PDF.</DialogDescription>
          </DialogHeader>
          {val && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[["Versione", `v${val.stats.versione} · ${val.stats.num_domande} dom.`], ["Compilate", val.stats.compilate], ["Punteggio", val.stats.punteggio != null ? `${val.stats.punteggio}%` : "n/d"], ["Non conformità", val.stats.non_conforme], ["Senza stato", val.stats.senza_stato], ["Non verificate", val.stats.non_verificato], ["N/A senza motivo", val.stats.na_senza_motivazione], ["Evidenze mancanti", val.stats.evidenze_mancanti], ["Critici/Alti NC", val.stats.controlli_critici_alti_nc], ["Azioni aperte", val.stats.azioni_aperte], ["Azioni scadute", val.stats.azioni_scadute], ["Allegati", val.stats.allegati]].map(([l, v]) => (
                  <div key={l} className="flex justify-between border-b border-border/60 py-0.5"><span className="text-muted-foreground">{l}</span><b>{v}</b></div>
                ))}
              </div>
              {val.errors?.length > 0 && (
                <div className="rounded-sm border border-red-200 bg-red-50 p-2.5" data-testid="nis2-finalize-errors">
                  <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1"><WarningOctagon size={13} /> Errori bloccanti ({val.errors.length})</div>
                  <ul className="text-xs text-red-700 list-disc pl-4 space-y-0.5">{val.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              {val.warnings?.length > 0 && (
                <div className="rounded-sm border border-amber-200 bg-amber-50 p-2.5" data-testid="nis2-finalize-warnings">
                  <div className="text-xs font-semibold text-amber-800 mb-1 flex items-center gap-1"><WarningCircle size={13} /> Avvisi ({val.warnings.length})</div>
                  <ul className="text-xs text-amber-800 list-disc pl-4 space-y-0.5">{val.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              {val.errors?.length === 0 && val.warnings?.length > 0 && (
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="nis2-finalize-confirm-check">
                  <Checkbox checked={conf} onCheckedChange={setConf} /> Confermo di voler finalizzare l'analisi con gli avvisi indicati.
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDlgOpen(false)} data-testid="nis2-finalize-cancel">Annulla</Button>
            <Button className="rounded-sm bg-primary" disabled={busy || !val || val.errors?.length > 0 || (val.warnings?.length > 0 && !conf)} onClick={doFinalize} data-testid="nis2-finalize-confirm">{busy ? "Finalizzazione…" : "Finalizza e genera PDF"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Executive summary */}
      <Card className="rounded-xl border-slate-100 shadow-sm p-5" data-testid="nis2-exec-summary">
        <div className="flex flex-col lg:flex-row gap-5 items-center lg:items-stretch">
          <div className="flex flex-col items-center justify-center px-4" data-testid="nis2-semaforo">
            <div className="rounded-full w-24 h-24 flex items-center justify-center text-white text-2xl font-bold" style={{ backgroundColor: sem.colore }}>
              {ex.overall_pct !== null ? `${ex.overall_pct}%` : "N/D"}
            </div>
            <div className="mt-2 text-sm font-semibold" style={{ color: sem.colore }}>{sem.label}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <KpiBox label="Domande totali" value={ex.domande_totali} />
              <KpiBox label="Controlli critici" value={ex.controlli_critici} accent="text-amber-600" />
              <KpiBox label="Non conformità" value={ex.non_conformita} accent="text-red-600" />
              <KpiBox label="Azioni aperte" value={ex.azioni_aperte} accent="text-blue-600" />
            </div>
            <p className="text-sm text-foreground/80 bg-secondary/40 rounded-lg p-3" data-testid="nis2-exec-text">{ex.testo}</p>
          </div>
        </div>
      </Card>

      {/* Grafici */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-chart-stati">
          <h3 className="font-heading text-sm font-semibold mb-2">Distribuzione degli stati</h3>
          {pieData.length === 0 ? <p className="text-xs text-muted-foreground py-10 text-center">Nessuna risposta valutabile</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`} labelLine={false} fontSize={10}>
                  {pieData.map((d, i) => <Cell key={i} fill={NIS2_STATO_PIE_COLORS[d.key]} />)}
                </Pie>
                <RTooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-chart-priorita">
          <h3 className="font-heading text-sm font-semibold mb-2">Conformità per priorità</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={prioData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
              <RTooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="pct" fill="#00396a" radius={[3, 3, 0, 0]}><LabelList dataKey="pct" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 10 }} /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-chart-aree">
          <h3 className="font-heading text-sm font-semibold mb-2">Conformità per area</h3>
          <ResponsiveContainer width="100%" height={Math.max(240, areaData.length * 26)}>
            <BarChart data={areaData} layout="vertical" margin={{ left: 10, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
              <RTooltip formatter={(v, n, p) => [`${v}% (${p.payload.domande} domande)`, "Conformità"]} />
              <Bar dataKey="pct" radius={[0, 3, 3, 0]}>
                {areaData.map((d, i) => <Cell key={i} fill={d.pct >= 85 ? "#16a34a" : d.pct >= 60 ? "#d97706" : "#dc2626"} />)}
                <LabelList dataKey="pct" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-chart-azioni">
          <h3 className="font-heading text-sm font-semibold mb-2">Azioni correttive</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={azData} margin={{ left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>{azData.map((d, i) => <Cell key={i} fill={d.fill} />)}<LabelList dataKey="value" position="top" style={{ fontSize: 10 }} /></Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Confronto revisione precedente */}
      {data.confronto && (
        <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-confronto">
          <h3 className="font-heading text-sm font-semibold mb-3">Confronto con {data.confronto.revisione_precedente}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            <KpiBox label="Conformità precedente" value={data.confronto.conformita_precedente !== null ? `${data.confronto.conformita_precedente}%` : "N/D"} />
            <KpiBox label="Conformità attuale" value={data.confronto.conformita_attuale !== null ? `${data.confronto.conformita_attuale}%` : "N/D"} accent="text-primary" />
            <div className="rounded-lg border border-slate-100 p-3 text-center"><div className="text-2xl font-bold"><Delta v={data.confronto.delta} suffix="%" /></div><div className="text-[11px] text-muted-foreground mt-0.5">Delta</div></div>
            <KpiBox label="NC risolte" value={data.confronto.non_conformita_risolte} accent="text-emerald-600" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Migliorati: <b className="text-emerald-600">{data.confronto.controlli_migliorati}</b></span>
            <span>Peggiorati: <b className="text-red-600">{data.confronto.controlli_peggiorati}</b></span>
            <span>Nuove NC: <b className="text-red-600">{data.confronto.nuove_non_conformita}</b></span>
            <span>Evidenze aggiunte: <b className="text-foreground">{data.confronto.evidenze_aggiunte}</b></span>
            <span>Evidenze ancora mancanti: <b className="text-foreground">{data.confronto.evidenze_ancora_mancanti}</b></span>
            <span>Azioni aperte attuali: <b className="text-foreground">{data.confronto.azioni_aperte_attuali}</b></span>
          </div>
        </Card>
      )}

      {/* Top criticità */}
      <Card className="rounded-xl border-slate-100 shadow-sm overflow-hidden" data-testid="nis2-top-criticita">
        <h3 className="font-heading text-sm font-semibold p-4 pb-3 flex items-center gap-1.5"><WarningOctagon size={16} className="text-red-600" /> Top criticità</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/50 text-muted-foreground border-y border-border">
              <tr><th className="px-3 py-2 text-left">#</th><th className="px-3 py-2 text-left">Domanda</th><th className="px-3 py-2 text-left">Area</th><th className="px-3 py-2 text-left">Priorità</th><th className="px-3 py-2 text-left">Stato</th><th className="px-3 py-2 text-left">Owner</th><th className="px-3 py-2 text-center">All.</th><th className="px-3 py-2 text-center">Az.</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {data.top_criticita.length === 0 ? <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground">Nessuna criticità rilevante</td></tr> : data.top_criticita.map((c) => (
                <tr key={c.numero} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`nis2-crit-row-${c.numero}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-primary">#{c.numero}</td>
                  <td className="px-3 py-2 max-w-[280px] truncate" title={c.domanda}>{c.domanda}</td>
                  <td className="px-3 py-2">{c.area}</td>
                  <td className="px-3 py-2">{NIS2_PRIO_LABEL[c.priorita] || c.priorita}</td>
                  <td className="px-3 py-2"><span className={NIS2_STATI_RISPOSTA.find((s) => s.v === c.stato)?.c || ""}>{NIS2_STATO_RISP_LABEL[c.stato] || "Senza stato"}</span></td>
                  <td className="px-3 py-2">{c.owner}</td>
                  <td className="px-3 py-2 text-center">{c.att_count}{c.evidenza_mancante ? <span className="text-red-600" title="Evidenza mancante"> !</span> : ""}</td>
                  <td className="px-3 py-2 text-center">{c.act_count}{c.act_overdue ? <span className="text-red-600" title="Azioni scadute"> ⏰</span> : ""}</td>
                  <td className="px-3 py-2"><Button size="sm" variant="ghost" className="h-7 rounded-sm text-xs text-primary" onClick={() => onGoTo(c.numero)} data-testid={`nis2-crit-goto-${c.numero}`}>Vai →</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Raccomandazioni */}
      <Card className="rounded-xl border-slate-100 shadow-sm p-4" data-testid="nis2-raccomandazioni">
        <h3 className="font-heading text-sm font-semibold mb-2 flex items-center gap-1.5"><CheckCircle size={16} className="text-primary" /> Raccomandazioni</h3>
        <ul className="space-y-1.5">
          {data.raccomandazioni.map((r, i) => <li key={i} className="text-sm flex items-start gap-2"><CaretRight size={14} className="text-primary mt-0.5 shrink-0" /> {r}</li>)}
        </ul>
      </Card>

      {/* Analisi per area */}
      <Card className="rounded-xl border-slate-100 shadow-sm overflow-hidden" data-testid="nis2-analisi-aree">
        <h3 className="font-heading text-sm font-semibold p-4 pb-3">Analisi per area</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/50 text-muted-foreground border-y border-border">
              <tr><th className="px-3 py-2 text-left">Area</th><th className="px-3 py-2 text-center">Conf.</th><th className="px-3 py-2 text-center">Dom.</th><th className="px-3 py-2 text-center">Conformi</th><th className="px-3 py-2 text-center">Parziali</th><th className="px-3 py-2 text-center">NC</th><th className="px-3 py-2 text-center">NV</th><th className="px-3 py-2 text-center">Ev. manc.</th></tr>
            </thead>
            <tbody>
              {sc.per_area.map((a) => (
                <tr key={a.nome} className="border-b border-border/60">
                  <td className="px-3 py-2 font-medium">{a.nome}</td>
                  <td className="px-3 py-2 text-center font-semibold" style={{ color: a.pct === null ? "#94a3b8" : a.pct >= 85 ? "#16a34a" : a.pct >= 60 ? "#d97706" : "#dc2626" }}>{a.pct === null ? "N/D" : `${a.pct}%`}</td>
                  <td className="px-3 py-2 text-center">{a.domande}</td>
                  <td className="px-3 py-2 text-center text-emerald-600">{a.conformi}</td>
                  <td className="px-3 py-2 text-center text-amber-600">{a.parziali}</td>
                  <td className="px-3 py-2 text-center text-red-600">{a.nc}</td>
                  <td className="px-3 py-2 text-center">{a.nv + a.senza_stato}</td>
                  <td className="px-3 py-2 text-center">{a.evidenze_mancanti}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
